import React, { useState, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { toast } from 'react-toastify';
import { CaretDown, CaretUp, X, FileText, Link as LinkIcon } from '@phosphor-icons/react';
import { Spinner } from '@heroui/react';

interface InvoiceLine {
  id: number;
  odooAnalyticLineId: number;
  odooMoveId?: number;
  invoiceNumber: string;
  description: string;
  amount: number;
  invoiceDate: string;
  supplier: string;
  pdfData?: string;
  pdfName?: string;
}

interface LinkedInvoicesSectionProps {
  ticketId: number;
  actualCost?: number;
  isExpanded?: boolean;
}

export const LinkedInvoicesSection: React.FC<LinkedInvoicesSectionProps> = ({
  ticketId,
  isExpanded = false
}) => {
  const { session } = useSession();
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState(isExpanded);

  useEffect(() => {
    if (ticketId && session) {
      fetchInvoiceLines();
    }
  }, [ticketId, session]);

  // Auto-expand if isExpanded prop changes
  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  const fetchInvoiceLines = async () => {
    if (!session) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}/invoice-lines`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setInvoiceLines(data);
      } else {
        console.error('Failed to fetch invoice lines:', response.status);
      }
    } catch (error) {
      console.error('Error fetching invoice lines:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlinkInvoice = async (lineId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/${lineId}/unlink-ticket`,
        { method: 'PUT' },
        session
      );

      if (response.ok) {
        // Remove from local state
        setInvoiceLines(prev => prev.filter(line => line.id !== lineId));
        toast.success('Factuurlijn succesvol ontkoppeld');

        // Refresh page to update actual cost
        window.location.reload();
      } else {
        toast.error('Fout bij ontkoppelen factuurlijn');
      }
    } catch (error) {
      console.error('Error unlinking invoice:', error);
      toast.error('Fout bij ontkoppelen factuurlijn');
    }
  };

  const totalCost = invoiceLines.reduce((sum, line) => sum + line.amount, 0);
  const hasInvoices = invoiceLines.length > 0;

  return (
    <div className="bg-muted border border-muted-foreground/20 rounded-lg overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <FileText size={20} weight="duotone" className="text-brick-primary" />
          <div className="text-left">
            <h3 className="text-base font-semibold text-neutral-primary">
              Gekoppelde Facturen
            </h3>
            <p className="text-xs text-neutral-secondary-mid mt-0.5">
              {hasInvoices ? `${invoiceLines.length} factuurlijn(en)` : 'Geen facturen gekoppeld'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {hasInvoices && (
            <div className="text-right">
              <p className="text-xs text-neutral-secondary-mid">Werkelijke Kost</p>
              <p className="text-lg font-bold text-moss-primary">
                {new Intl.NumberFormat('nl-BE', {
                  style: 'currency',
                  currency: 'EUR'
                }).format(totalCost)}
              </p>
            </div>
          )}
          {expanded ? (
            <CaretUp size={20} className="text-neutral-secondary-mid" />
          ) : (
            <CaretDown size={20} className="text-neutral-secondary-mid" />
          )}
        </div>
      </button>

      {/* Content - Expandable */}
      {expanded && (
        <div className="border-t border-neutral-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : hasInvoices ? (
            <div className="divide-y divide-neutral-100">
              {invoiceLines.map((line) => (
                <div key={line.id} className="px-6 py-4 hover:bg-neutral-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Invoice info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {line.odooMoveId ? (
                          <a
                            href={`https://portal.yally.be/odoo/vendor-bills/${line.odooMoveId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-moss-primary hover:underline flex items-center gap-1"
                          >
                            {line.invoiceNumber}
                            <LinkIcon size={12} />
                          </a>
                        ) : (
                          <span className="text-sm font-semibold text-neutral-primary">
                            {line.invoiceNumber}
                          </span>
                        )}
                        <span className="text-xs text-neutral-secondary-mid">
                          {new Date(line.invoiceDate).toLocaleDateString('nl-BE')}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-700 mb-1">{line.description}</p>
                      <p className="text-xs text-neutral-secondary-mid">{line.supplier}</p>
                    </div>

                    {/* Right: Amount and actions */}
                    <div className="flex items-center gap-3">
                      <p className="text-base font-semibold text-neutral-primary">
                        {new Intl.NumberFormat('nl-BE', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(Math.abs(line.amount))}
                      </p>
                      <button
                        onClick={() => handleUnlinkInvoice(line.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Ontkoppel factuurlijn"
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <FileText size={48} className="mx-auto text-neutral-300 mb-3" weight="thin" />
              <p className="text-sm text-neutral-secondary-mid">
                Nog geen facturen gekoppeld aan dit ticket
              </p>
              <p className="text-xs text-neutral-secondary-mid mt-1">
                Koppel factuurlijnen via de OPEX facturen pagina
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
